import * as React from 'react';
import {Helmet} from 'react-helmet';

import classnames from 'classnames';

import {headTitle} from 'shells/document/head-title';
import {Stack} from 'shells/layout/stack/stack';
import {Body} from 'shells/typography/body';

import {GlossarySidebarButton} from '../../../../../ui/glossary/sidebar/glossary-sidebar-button';

import styles from './page-header-full.module.css';

export type Props = {
    tabTitle?: string;
    title: string;
    subtitle: string;
    illustration: React.ReactNode;
    glossaryKey?: string;
    illustrationAlignment?: 'center' | 'end' | 'start';
    maxWidth?: 800 | 1000 | 1200 | 1400 | 1600;
    height?: 200 | 240 | 280 | 320 | 360;
    xPadding?: 16 | 24 | 32 | 40 | 48 | 64;
    backgroundColorVariant?: 'blue' | 'purple';
    additionalContent?: React.ReactNode;
};

export function PageHeaderFull(props: Props) {
    const {
        height = 320,
        illustrationAlignment = 'center',
        xPadding = 64,
        backgroundColorVariant = 'blue',
    } = props;

    const illustrationClassNames = classnames('flex', 'full-height', {
        'align-end': illustrationAlignment === 'end',
        'align-start': illustrationAlignment === 'start',
        'align-center': illustrationAlignment === 'center',
    });

    const headerContentStyle = {
        paddingLeft: `${xPadding}px`,
        paddingRight: `${xPadding}px`,
        maxWidth: props.maxWidth ? `${props.maxWidth}px` : undefined,
    } as const;

    const headerContainerClassNames = classnames(styles['header-container'], {
        [styles['header-blue']]: backgroundColorVariant === 'blue',
        [styles['header-purple']]: backgroundColorVariant === 'purple',
    });

    const headerContentClassNames = classnames(styles['header-content'], {
        [styles['header-blue']]: backgroundColorVariant === 'blue',
        [styles['header-purple']]: backgroundColorVariant === 'purple',
    });

    return (
        <>
            <Helmet>
                <title>{headTitle(props.tabTitle ?? props.title)}</title>
            </Helmet>
            <div
                style={{
                    height: `${height}px`,
                }}
                className={headerContainerClassNames}
            >
                <div style={headerContentStyle} className={headerContentClassNames}>
                    <Stack
                        isHorizontal={true}
                        alignItems='center'
                        isFullWidth={true}
                        spacing={8}
                        justifyContent='space-between'
                    >
                        <div className='half-width'>
                            <Stack spacing={16}>
                                <Stack isHorizontal={true} spacing={8} alignItems='center'>
                                    <div className={styles['header-text']}>{props.title}</div>
                                    <GlossarySidebarButton glossaryKey={props.glossaryKey} />
                                </Stack>
                                <Body color='dark' size='large'>
                                    {props.subtitle}
                                </Body>
                                {props.additionalContent}
                            </Stack>
                        </div>
                        <div className={illustrationClassNames}>
                            {props.illustration && props.illustration}
                        </div>
                    </Stack>
                </div>
            </div>
        </>
    );
}
